import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { glossary } from '@sorare/core/src/lib/glossary';
import { regularMaterial, thinMaterial } from '@sorare/core/src/style/utils';

import { getCompetitionPictureUrl } from '@sorare/gameplay/src/lib/so5';

import type { CompetitionTile_onboardingLeaderboard } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 0.5, center: true })`
  position: relative;
  padding: var(--quadruple-unit) var(--double-unit) var(--triple-unit);
  border-radius: var(--unit);
  background: var(--c-nl-500);
  ${regularMaterial}
  border: 1px solid var(--c-nd-50);
  height: 100%;

  &.disabled {
    opacity: 0.5;
  }
  &:not(.disabled):hover {
    background: var(--c-nl-300);
  }
  transition: 300ms background-color ease-in-out;
  isolation: isolate;
`;
const Icon = styled.img`
  width: 36px;
  height: 36px;
  object-fit: contain;
`;
const UnavailableChip = styled(Chip)`
  position: absolute;
  z-index: 1;
  left: var(--unit);
  top: var(--half-unit);
  ${thinMaterial}
`;
const CompetitionIcon = styled.img`
  width: var(--double-and-a-half-unit);
  height: var(--double-and-a-half-unit);
  object-fit: contain;
`;
const StyledHorizontal = styled(Horizontal).attrs({ center: true })`
  flex-wrap: wrap;
`;

type Props = {
  onClick: ({
    so5LeaderboardId,
    so5LeaderboardSlug,
    displayName,
  }: {
    so5LeaderboardId: string;
    so5LeaderboardSlug: string;
    displayName: string;
  }) => void;
  onboardingLeaderboard: CompetitionTile_onboardingLeaderboard;
  isMainLeaderboard: boolean;
};
export const CompetitionTile = ({
  onClick,
  onboardingLeaderboard,
  isMainLeaderboard,
}: Props) => {
  const { playableLeaderboard, pictureUrl, displayName } =
    onboardingLeaderboard;
  const { so5League } = playableLeaderboard || {};
  const { leagueSummary, competitions } = so5League || {};

  const disabled = !playableLeaderboard;

  return (
    <ButtonBase
      onClick={() => {
        if (disabled) {
          return;
        }
        onClick({
          so5LeaderboardId: playableLeaderboard.id,
          so5LeaderboardSlug: playableLeaderboard.slug,
          displayName,
        });
      }}
    >
      <Root className={classNames({ disabled })}>
        {!!disabled && (
          <UnavailableChip>
            <LabelM color="var(--c-white)" bold>
              <FormattedMessage {...glossary.offSeason} />
            </LabelM>
          </UnavailableChip>
        )}
        <Vertical center gap={1.5}>
          <Icon src={pictureUrl} alt="" />
          <LabelS bold className="text-center">
            {displayName}
          </LabelS>
        </Vertical>
        {!isMainLeaderboard && (
          <Vertical gap={2}>
            <LabelS color="var(--c-nd-600)">{leagueSummary}</LabelS>
            <StyledHorizontal gap={1.5}>
              {competitions?.map(c => {
                if (!c) {
                  return null;
                }
                const competitionPictureUrl = getCompetitionPictureUrl(c);

                return (
                  <CompetitionIcon
                    src={competitionPictureUrl}
                    key={c.slug}
                    alt=""
                  />
                );
              })}
            </StyledHorizontal>
          </Vertical>
        )}
      </Root>
    </ButtonBase>
  );
};

CompetitionTile.fragments = {
  onboardingLeaderboard: gql`
    fragment CompetitionTile_onboardingLeaderboard on OnboardingLeaderboard {
      id
      pictureUrl
      displayName
      playableLeaderboard {
        id
        slug
        so5League {
          slug
          shortDescription
          leagueSummary
          competitions {
            slug
            ...getCompetitionPictureUrl_competition
          }
        }
      }
    }
    ${getCompetitionPictureUrl.fragments.competition}
  ` as TypedDocumentNode<CompetitionTile_onboardingLeaderboard>,
};
